import { h, Component } from 'preact';
import Store from 'store/store';
import Calendar from 'components/bolt/icons/calendar';
import Clock from 'components/bolt/icons/clock';
import Reel from 'components/bolt/icons/reel';
import Link from 'components/bolt/link/link';
import Button from 'components/bolt/button/button';

import { accessNested, joinClasses } from 'utils/utils';
import { prioritiseTicketProviders, convertTimeTo12Hr } from 'utils/bolt/showtimes';
import formatDateTime from 'utils/formatDateTime';
import s from 'components/bolt/showtimes/providerInterstitial.sss';

export default class ProviderInterstitial extends Component {
	constructor(props) {
		super(props);
		this.trackConversion = this.trackConversion.bind(this);
		this.triggerProviderBtn = this.triggerProviderBtn.bind(this);

		this.links = props.selectedScreening.link;

		this.providers = prioritiseTicketProviders(Object.keys(this.links));
		this.state = {
			deeplink: this.links[this.providers[0]],
			provider: this.providers[0]
		};

		this.fontStyles = accessNested(Store.get(), 'appData.styling.fontVariables') || {};
	}

	changeDeepLink(link, provider) {
		this.setState({
			deeplink: link,
			provider: provider
		});
	}

	triggerProviderBtn(provider) {
		if (this.state.provider === provider) {
			if (this.deeplink && this.deeplink.base) this.deeplink.base.click();
		} else {
			this.changeDeepLink(this.links[provider], provider);
		}
	}

	trackConversion(url) {
		if (url && typeof url !== 'string') {
			url = url.target.href;
		}
		let props = this.props;
		props.trackConversion?.(props.selectedTheater, props.selectedScreening, this.state.provider, props.mode, url);
	}

	renderMobileVersion() {
		const { isPreviewPanel } = this.props;

		return (
			<div class={joinClasses(s.container, isPreviewPanel && s.previewPanel)}>
				{this.renderHeader()}
				{this.renderScreeningDetails()}
				{this.renderProviderOptions()}
				{this.renderLinkButton()}
			</div>
		);
	}

	renderDesktopVersion() {
		const theaterName = accessNested(this.props, 'selectedTheater.name', 'Cinema');
		const { isPreviewPanel } = this.props;

		return (
			<div class={joinClasses(s.container, isPreviewPanel && s.previewPanel)}>
				{isPreviewPanel && (
					<div class={s.shadow} onClick={() => {
						Store.emit('CLOSE_PREVIEW_PANEL_PROVIDER_INTERSTITIAL');
					}} />
				)}
				<div class={s.optionsColumn}>
					{this.renderHeader()}
					{this.renderProviderOptions()}
				</div>
				<div class={s.selectionColumn}>
					<div class={s.selectionHeader}>
						{this.renderTheaterLogo()}
						<span class={this.fontStyles.providerTheaterName}>{theaterName}</span>
					</div>
					{this.renderScreeningDetails()}
					{this.renderLinkButton()}
				</div>
			</div>
		);
	}

	renderHeader() {
		return (
			<div class={s.header}>
				{ !this.props.isDesktopSize && this.renderTheaterLogo() }
				<h4 class={this.fontStyles.providerWhereToBuy}>{this.props.copy.baseCopy.$WHERE_TO_BUY || 'Where to buy?'}</h4>
			</div>
		);
	}

	renderTheaterLogo() {
		const logoAlt = accessNested(Store.get(), 'appData.copy.accessibilityCopy.$CINEMA_LOGO_ALT', '<chain>');
		const logo = accessNested(this.props, 'selectedTheater.chain.logo') || 'default.png';
		const logoLink = `${this.props.sharedImageRoot}/logos/${this.props.logoShade}/${logo}`;
		const strippedChainName = this.props.selectedTheater.chain && this.props.selectedTheater.chain.name.replace(/\..*/, '');

		return (
			<div class={s.theaterLogo}>
				<img src={logoLink} alt={logoAlt.replace('<chain>', strippedChainName)} />
			</div>
		);
	}

	renderScreeningDetails() {
		let { date, time, format, attributes } = this.props.selectedScreening;

		const dateParts = date.split('-');
		let screeningDate = new Date(+dateParts[0], dateParts[1] - 1, +dateParts[2]);
		screeningDate = formatDateTime(screeningDate, 'ddd dd MMM');

		const screeningAttributes = [...attributes];
		const attributesCopy = this.props.copy?.baseCopy?.filters?.attributes || {};

		if (accessNested(Store.get(), 'appData.pages.boltShowtimes.data.clockFormat', '24') === '12') {
			time = convertTimeTo12Hr(time);
		}

		return (
			<div class={s.screeningDetails}>
				<div class={s.mainDetails}>
					<div class={s.date}>
						<Calendar ariaLabel="Calendar" />
						<span class={this.fontStyles.providerDateTimeFormat}>{screeningDate}</span>
					</div>
					<div class={s.time}>
						<Clock ariaLabel="Clock" />
						<span class={this.fontStyles.providerDateTimeFormat}>{time}</span>
					</div>
					<div class={s.format}>
						<Reel ariaLabel="Reel" />
						<span class={this.fontStyles.providerDateTimeFormat}>{format || '2D'}</span>
					</div>
				</div>
				<div class={s.attributes}>
					{screeningAttributes.map(attribute => (
						<span>{attributesCopy[attribute] || attribute}</span>
					))}
				</div>
			</div>
		);
	}

	renderProviderOptions() {
		return (
			<div class={s.providerContainer}>
				<span class={joinClasses(s.whereToPurchaseText, this.fontStyles.providerSelectWhere)}>{this.props.copy.baseCopy.$SELECT_WHERE_TO_PURCHASE || 'Select where to purchase your tickets'}</span>
				{this.providers.map(provider => this.renderProvider(provider))}
			</div>
		);
	}

	renderProvider(provider) {
		const { sharedImageRoot, logoShade, copy } = this.props;
		const theaterLogo = accessNested(this.props, 'selectedTheater.chain.logo') || 'default.png';
		const theaterLogoLink = `${sharedImageRoot}/logos/${logoShade}/${theaterLogo}`;
		let providerAriaLabel = copy.accessibilityCopy.$PROVIDER_OPTION_ARIA;
		let name;
		if (provider === 'direct') {
			name = <span>{copy.baseCopy.$DIRECT_FROM_CINEMA || 'Direct from cinema'}</span>;
			providerAriaLabel = providerAriaLabel.replace('<provider>', this.props.selectedTheater.name);
		} else {
			providerAriaLabel = providerAriaLabel.replace('<provider>', provider);
		}
		let imgSrc = provider === 'direct' ? theaterLogoLink : `${sharedImageRoot}/logos/ticketproviders/fullsize/${logoShade}/${provider}.png`;

		return (
			<Button
				class={joinClasses({ s, providerBtn: true, providerBtnActive: this.state.provider === provider })}
				trigger={() => this.triggerProviderBtn(provider)}
				key={provider}
			>
				<div class={joinClasses(s.provider, this.fontStyles.providerDirect)} ariaLabel={providerAriaLabel}>
					<div class={joinClasses({ s, providerLogo: true, directFromTheater: provider === 'direct' })}>
						<img src={imgSrc} />
					</div>
					{name}
				</div>
			</Button>
		);
	}

	renderLinkButton() {
		const { selectedTheater } = this.props;
		const { provider } = this.state;

		let linkAriaLabel = this.props.copy.accessibilityCopy.$GO_TO_PROVIDER_SITE_ARIA;

		if (provider === 'direct') {
			linkAriaLabel = linkAriaLabel.replace('<provider>', selectedTheater.name);
		} else {
			linkAriaLabel = linkAriaLabel.replace('<provider>', provider);
		}

		return (
			<div class={s.linkContainer}>
				<Link
					ref={ref => this.deeplink = ref}
					href={this.state.deeplink}
					class={s.linkBtn}
					target="_blank"
					rel="noopener noreferrer"
					ariaLabel={linkAriaLabel}
					onClick={this.trackConversion}
				>
					<span class={this.fontStyles.providerLink}>{this.props.copy.baseCopy.$GO_TO_SITE || 'Go to Site'}</span>
				</Link>
			</div>
		);
	}


	render(props) {
		return props.isDesktopSize ? this.renderDesktopVersion() : this.renderMobileVersion();
	}
}
